/**
 * Notifications API Module
 * Handles all API calls for the notifications panel
 */

import apiClient from './api-client.js';
import { formatTimeRelativeShort, formatTimeRelativeLong } from './time-utils.js';

/**
 * Make API request and return JSON
 * @param {string} endpoint
 * @param {object} options
 * @returns {Promise<any>}
 */
async function apiRequest(endpoint, options = {}) {
    const response = await apiClient.request(endpoint, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...options.headers
        }
    });

    if (!response.ok) {
        const error = new Error(`API request failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }

    return response.json();
}

/**
 * Fetch user counters (notifications, friend requests, views)
 * @returns {Promise<{new_friend_requests: number, new_notifications: number, new_views: number}>}
 */
export async function fetchUserCounters() {
    return apiRequest('/user-counters');
}

/**
 * Fetch notifications (activity feed)
 * @param {number} limit
 * @param {number} offset
 * @returns {Promise<{data: Array}>}
 */
export async function fetchNotifications(limit = 10, offset = 0) {
    const params = new URLSearchParams({ limit, offset });
    return apiRequest(`/notification/my?${params}`);
}

/**
 * Fetch incoming friend requests
 * @param {string} userId
 * @param {number} limit
 * @param {number} offset
 * @returns {Promise<{data: Array}>}
 */
export async function fetchFriendRequests(userId, limit = 25, offset = 0) {
    const params = new URLSearchParams({ type: 'incoming', limit, offset });
    return apiRequest(`/user/${userId}/relations?${params}`);
}

/**
 * Accept friend request
 * @param {string} userId - Target user ID to accept
 * @returns {Promise<object>}
 */
export async function acceptFriendRequest(userId) {
    return apiRequest(`/relation/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'accept_friend' })
    });
}

/**
 * Ignore friend request
 * @param {string} userId - Target user ID to ignore
 * @returns {Promise<object>}
 */
export async function ignoreFriendRequest(userId) {
    return apiRequest(`/relation/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'ignore_friend' })
    });
}

/**
 * Fetch profile views
 * @param {string} userId
 * @param {number} limit
 * @param {number} offset
 * @returns {Promise<{data: Array}>}
 */
export async function fetchProfileViews(userId, limit = 20, offset = 0) {
    const params = new URLSearchParams({ limit, offset });
    return apiRequest(`/views/${userId}?${params}`);
}

/**
 * Mark a single notification as read
 * @param {string} notificationId
 * @returns {Promise<object>}
 */
export async function markNotificationRead(notificationId) {
    return apiRequest(`/notification/${notificationId}/mark-read`, {
        method: 'PUT'
    });
}

/**
 * Mark all notifications as read
 * @returns {Promise<void>}
 */
export async function markAllNotificationsRead() {
    await apiClient.request('/notification/self/mark-all-read', {
        method: 'PUT'
    });
}

/**
 * Reset views counter
 * @param {string} userId
 * @returns {Promise<void>}
 */
export async function resetViewsCounter(userId) {
    await apiClient.request(`/views-stats/${userId}/reset`, {
        method: 'PUT'
    });
}

/**
 * Get notification click URL based on type
 * @param {object} notification
 * @returns {string}
 */
export function getNotificationUrl(notification) {
    if (notification.click_url) {
        return notification.click_url;
    }

    const type = notification.type;
    const user = notification.user;

    if (type === 'FRIEND_REQUEST_MESSAGE' || type === 'FRIEND_ACCEPT_MESSAGE') {
        return user ? `/${user.username}` : '/app/friends';
    }

    if (type.startsWith('LIKE_') || type.startsWith('COMMENT_')) {
        if (notification.post) {
            return notification.post.click_url || `/app/posts/${notification.post.id}`;
        }
        if (notification.photo) {
            return `/photo/${notification.photo.id}`;
        }
    }

    return user ? `/${user.username}` : '/';
}

/**
 * Get icon and color for notification type
 * @param {string} type
 * @returns {{icon: string, color: string, bgColor: string}}
 */
export function getNotificationIcon(type) {
    const icons = {
        'FRIEND_REQUEST_MESSAGE': { icon: 'user-plus', color: 'text-white', bgColor: 'bg-orange-400' },
        'FRIEND_ACCEPT_MESSAGE': { icon: 'user-check', color: 'text-white', bgColor: 'bg-green-500' },
        'FRIEND_BIRTHDAY': { icon: 'cake', color: 'text-white', bgColor: 'bg-orange-400' },
        'LIKE_PHOTO': { icon: 'heart', color: 'text-white', bgColor: 'bg-rose-500' },
        'LIKE_POST': { icon: 'heart', color: 'text-white', bgColor: 'bg-rose-500' },
        'LIKE_ALBUM': { icon: 'heart', color: 'text-white', bgColor: 'bg-rose-500' },
        'LIKE_COMMENT': { icon: 'heart', color: 'text-white', bgColor: 'bg-rose-500' },
        'COMMENT_PHOTO': { icon: 'message-circle', color: 'text-white', bgColor: 'bg-sky-500' },
        'COMMENT_POST': { icon: 'message-circle', color: 'text-white', bgColor: 'bg-sky-500' },
        'COMMENT_ALBUM': { icon: 'message-circle', color: 'text-white', bgColor: 'bg-sky-500' },
        'COMMENT_REPLY': { icon: 'reply', color: 'text-white', bgColor: 'bg-sky-500' },
        'PROFILE_VIEW': { icon: 'eye', color: 'text-white', bgColor: 'bg-purple-500' }
    };

    return icons[type] || { icon: 'bell', color: 'text-white', bgColor: 'bg-gray-400' };
}

/**
 * Get inline SVG badge icon for notification type (small white icons)
 * @param {string} type
 * @returns {string} SVG HTML string
 */
export function getNotificationBadgeSvg(type) {
    const svgs = {
        // Friend request - people with plus icon
        'FRIEND_REQUEST_MESSAGE': '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>',
        // Friend accepted - checkmark
        'FRIEND_ACCEPT_MESSAGE': '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
        // Birthday - cake
        'FRIEND_BIRTHDAY': '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1"/><path d="M2 21h20"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>',
        // Heart - filled
        'LIKE_PHOTO': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
        'LIKE_POST': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
        'LIKE_ALBUM': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
        'LIKE_COMMENT': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
        // Comment bubble
        'COMMENT_PHOTO': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
        'COMMENT_POST': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
        'COMMENT_ALBUM': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
        'COMMENT_REPLY': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
        // Eye
        'PROFILE_VIEW': '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>'
    };

    return svgs[type] || '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';
}

/**
 * Get notification action text
 * @param {string} type
 * @param {string} [gender] - User gender for localized verb forms ('male' or 'female')
 * @returns {string}
 */
export function getNotificationActionText(type, gender) {
    const t = window.t || ((key) => key);

    const keys = {
        'FRIEND_REQUEST_MESSAGE': 'common.activity_wants_to_be_friend',
        'FRIEND_ACCEPT_MESSAGE': 'common.activity_accepted_friend_request',
        'FRIEND_BIRTHDAY': 'common.activity_has_birthday_today',
        'LIKE_PHOTO': 'common.activity_liked_your_photo',
        'LIKE_POST': 'common.activity_liked_your_post',
        'LIKE_ALBUM': 'common.activity_liked_your_album',
        'LIKE_COMMENT': 'common.activity_liked_your_comment',
        'COMMENT_PHOTO': 'common.activity_commented_on_your_photo',
        'COMMENT_POST': 'common.activity_commented_on_your_post',
        'COMMENT_ALBUM': 'common.activity_commented_on_your_album',
        'COMMENT_REPLY': 'common.activity_replied_to_your_comment',
        'PROFILE_VIEW': 'common.activity_viewed_your_profile',
        'FEED_NEW_POST': 'common.activity_posted_something_new'
    };

    const key = keys[type] || 'common.activity_sent_you_notification';
    return t(key, gender ? { gender } : {});
}

// Re-export time utilities for backwards compatibility
export { formatTimeRelativeLong as formatRelativeTime, formatTimeRelativeShort as formatRelativeTimeConcise } from './time-utils.js';

export default {
    fetchUserCounters,
    fetchNotifications,
    fetchFriendRequests,
    acceptFriendRequest,
    ignoreFriendRequest,
    fetchProfileViews,
    markNotificationRead,
    markAllNotificationsRead,
    resetViewsCounter,
    getNotificationUrl,
    getNotificationIcon,
    getNotificationBadgeSvg,
    getNotificationActionText,
    formatRelativeTime: formatTimeRelativeLong,
    formatRelativeTimeConcise: formatTimeRelativeShort
};
